import { canWhatsApp } from './wa-access.js';
import { canAssets } from './assets-access.js';
import { hasFeature } from './features.js';
import { allProjects, allClients, allEnquiries, allInvoices, allUsers, allSuppliers, allTimesheets, listKnowledgeSources, addNote, allInstructionsIndex, allDocumentsIndex, listInstructions, createTask, planTask, askLibrarian, waSearch } from './db.js';
import { parseTaskLine, chipsOf, dueLabel } from './task-parse.js';
import { dayKey } from './tasks.js';
import { deleteDoc, doc as fsDoc } from './fb/firestore.js';
import { db as fsDb } from './firebase.js';
import { ORG_ID } from './config.js';
import { escapeHtml, refTail, money, titleCase } from './ui.js';
import { openReportDialog } from './telemetry.js';
import { openProjectThread } from './views/golden-thread-open.js';
import { navIcon } from './nav-icons.js';
import { isPerson } from './people.js';
import { settingsV2On } from './settings-gate.js';   // Settings stage 1: ⌘K finds settings rows
import { callerTier, visibleRows } from './settings-registry.js';
import { searchSettings } from './settings-search.js';
import { orgSetting, orgSettings, orgInfo, isContractor } from './org.js';
import { financeFy27 } from './fin-pilot.js';   // a room screen is never @mentioned or given a task (v4.7.2)

// The Ask Track bar (handoff-ask-track §8, artboards 19a/20a/20b/21a/21b).
// A 52px navy disc, bottom right, that expands IN PLACE into a 420px navy
// panel — one surface, no centred overlay. Three modes in that panel:
//   Find — projects, clients, enquiries, suppliers, people, screens (invoices
//     for finance roles). Results stay in the panel; they are navigation.
//     Never standards content. (Instructions and files need an index that
//     doesn't exist yet — not faked.)
//   Ask Track — same words, handed to the librarian. Enter opens /library
//     with the query; the panel NEVER renders an answer itself.
//   Note — write a note from anywhere and land it on a project without
//     navigating there. A note cannot save without a project (21a state 04).
// The mode switches itself: no record matched + reads like a standards
// question → Ask Track, words intact, footer says why. Tab overrides the
// guess in either direction and never loses what is typed.
// One instance, mounted in the shell. Replaces the old floating pill.

const MONEY_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];
const SCREENS = [
  ['home', 'Home'], ['time', 'Timesheet'], ['projects', 'Projects'], ['calendar', 'Calendar'],
  ['enquiries', 'Enquiries'], ['clients', 'Clients'], ['suppliers', 'Suppliers'], ['team', 'Team'],
  ['summary', 'Finance Summary'], ['invoices', 'Invoices'], ['expenses', 'Expenses'],
  ['approvals', 'Approvals (timesheets)'], ['items', 'Items'], ['services', 'Services'],
  ['feedback', 'Feedback & Complaints'], ['procurement', 'Purchase orders'], ['library', 'Ask Track — ASP knowledge'],
];
const SCREEN_LABEL = Object.fromEntries(SCREENS.map(([id, l]) => [id, l.split(' (')[0].split(' — ')[0]]));
const EASE = 'var(--ease-out)';
const HAIR = 'var(--ab-hair)';   // v4: the palette is themed by --ab-* (styles.css)
const MODES = ['find', 'ask', 'note', 'task'];
const MODE_LABEL = { find: 'Find', ask: 'Ask Track', note: 'Note', task: 'Task' };
const PLACEHOLDER = {
  find: 'Search, or type a project number',
  ask: 'Ask the standards, or ask about a project…',
  note: 'What do you want to remember?',
  task: 'Add a task…',
};

/**
 * THE MODE SWITCHES ITSELF (§7).
 *
 * Nobody should have to tell a search box what kind of thing they are doing —
 * the words already say. Four digits is a project. A sentence ending in a
 * question mark is a question. Forty characters of prose that is not a question
 * is something you want remembered. Everything else is a name, so: Find.
 *
 * Read on every keystroke, and OVERRIDABLE: ⇥ cycles the mode and holds it
 * until the field is emptied, because the one thing worse than guessing wrong
 * is guessing wrong again after being corrected.
 *
 * Task is not inferred. It is a mode you choose, because "add a task" and "make
 * a note" are the same sentence until you say which you meant.
 */
const QUESTION_OPENERS = /^(what|how|which|who|when|do|does|is|are|can|should|must)\b/i;
const ENGINEERING = /(u-?value|span|load|deflection|rebar|bearing|part\s?[a-l]\b|eurocode|bs\s?\d|nhbc)/i;
export function inferMode(text) {
  const q = String(text || '').trim();
  if (!q) return 'find';
  if (/^\d{2,4}$/.test(q) || /^[Ee]-?\d/.test(q)) return 'find';
  if (q.endsWith('?') || QUESTION_OPENERS.test(q) || ENGINEERING.test(q)) return 'ask';
  if (q.length > 40) return 'note';
  return 'find';
}

// A question about WHY something happened is a question about the record, and
// the record is the golden thread (Track Golden Thread.html, 2b entry 04). So a
// why/when/who question that lands on a project opens the thread with the
// question pinned above it, rather than dropping you on the project page to go
// looking — and the answer is written from that project's OWN records by
// askRecords (functions/ask-records.js), which cites the one record it came
// from so the thread opens there. The two engines do not overlap: the library
// says what a standard requires, the records say what happened here. A why
// question we cannot pin to a job still goes to the library, which is the only
// engine that could try.
const WHY = /^(why|when did|who (decided|instructed|agreed|signed)|what happened|how did)\b/i;
const isRecordQuestion = (q) => WHY.test(String(q || '').trim());

let context = { screen: 'home', project: null };   // maintained by the shell
export function setAskContext(next) { context = { ...context, ...next }; }

export function mountAskBar({ session, open }) {
  if (document.querySelector('.askbar-root')) return;
  const email = String(session.email || '').toLowerCase();
  const myName = titleCase(session.displayName || email.split('@')[0].replace(/[._]+/g, ' '));
  const isMoney = !!(session.roles || []).some((r) => MONEY_ROLES.includes(r));
  const RECENTS_KEY = `askbar-recents-${email}`;
  const MODE_KEY = 'askbar-mode';                   // per session (20b)

  const root = document.createElement('div');
  root.className = 'askbar-root';
  root.setAttribute('style', 'position:fixed;right:26px;bottom:26px;z-index:900;display:flex;flex-direction:column;align-items:flex-end;gap:14px;font-family:var(--font-sans);');
  document.body.appendChild(root);

  // Saved-note confirmation card (21a state 05) sits above the disc for 6s.
  const toastHost = document.createElement('div');
  root.appendChild(toastHost);

  const surface = document.createElement('div');
  root.appendChild(surface);

  // Task mode binds by DELEGATION rather than per element. paint() and
  // repaintKeepingField() each rebind their own handlers, and the first cut of
  // this bound only in paint() — so a chip's × worked once and then silently
  // stopped. One listener on the surface cannot fall out of step with either.
  surface.addEventListener('mousedown', (ev) => {
    if (state.mode !== 'task') return;
    const pick = ev.target.closest('.askbar-tpick');
    if (pick) { ev.preventDefault(); state.taskPick = Number(pick.dataset.i); taskChoose(); return; }
    const x = ev.target.closest('.askbar-chip-x');
    if (x) { ev.preventDefault(); dropChip(x.dataset.drop); return; }
    if (ev.target.closest('.askbar-tplan')) { ev.preventDefault(); saveTask({ plan: true }); }
  });

  const state = {
    openState: false, mode: sessionStorage.getItem(MODE_KEY) || 'find',
    q: '', results: [], sel: 0, autoswitched: null,   // {from, reason} when Track moved modes itself
    data: null, loading: false, sourceCount: null, weekProjects: null,
    noteTarget: null, prevFocus: null,
    // task mode: which picker row is highlighted, whether the person has
    // unlinked the project by hand, and the last save error.
    taskPick: 0, taskUnlinked: false, taskError: null,
    // ⇥ pins the mode; emptying the field un-pins it (§7).
    held: false,
    // The answer, rendered IN the panel (§7): { asking, out, error }.
    ask: null,
  };
  if (!MODES.includes(state.mode)) state.mode = 'find';

  const recents = () => { try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]'); } catch (_) { return []; } };
  const remember = (r) => {
    const list = [{ type: r.type, id: r.id, ref: r.ref || '', title: r.title, sub: r.subType || r.type }, ...recents().filter((x) => x.id !== r.id)].slice(0, 3);
    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list)); } catch (_) { /* ignore */ }
  };

  // ── data (lazy, role-guarded like the old search: a denied collection just
  // yields [] so Find still works for every role) ──
  async function ensureData() {
    if (state.data || state.loading) return;
    state.loading = true;
    try {
      const [projects, clients, enquiries, users, suppliers, invoices, sources, instructions, files] = await Promise.all([
        allProjects().catch(() => []), allClients().catch(() => []), allEnquiries().catch(() => []),
        allUsers().catch(() => []), allSuppliers().catch(() => []),
        isMoney ? allInvoices().catch(() => []) : Promise.resolve([]),
        listKnowledgeSources().catch(() => []),
        allInstructionsIndex().catch(() => []),   // needs the collection-group rule; [] until deployed
        allDocumentsIndex().catch(() => []),
      ]);
      const clientName = Object.fromEntries(clients.map((c) => [c.id, c.name]));
      const projById = Object.fromEntries(projects.map((p) => [p.id, p]));
      state.data = { projects, clients, enquiries, users, suppliers, invoices, clientName, instructions, files, projById };
      state.sourceCount = sources.length;
    } finally { state.loading = false; }
    // Note mode resolves its target FROM this data. Opening the bar straight
    // into Note (the mode is remembered per session) computed the target before
    // the data existed, so the footer said "no project on the screen behind"
    // until you typed a character — on a screen that had a project all along.
    if (state.mode === 'note' && !state.noteTarget) state.noteTarget = noteTarget();
    if (state.openState) paint();
  }
  // Projects I logged time to this week — the note fallback list (21a).
  async function ensureWeekProjects() {
    if (state.weekProjects) return;
    try {
      const sheets = await allTimesheets(40);
      const monday = (() => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return dayKey(d); })();   // local, not UTC
      const ids = [];
      for (const s of sheets) {
        if (String(s.userEmail || '').toLowerCase() !== email) continue;
        if (String(s.weekStart || '') < monday) continue;
        for (const r of (s.rows || [])) if (r.projectId && !ids.includes(r.projectId)) ids.push(r.projectId);
      }
      await ensureData();
      state.weekProjects = ids.map((id) => (state.data.projects || []).find((p) => p.id === id)).filter(Boolean).slice(0, 3);
    } catch (_) { state.weekProjects = []; }
    if (state.openState) paint();
  }

  // ── scoring (multi-word AND, carried over from the old universal search) ──
  function score(q, ...fields) {
    const terms = q.split(/\s+/).filter(Boolean);
    const vals = fields.map((f) => String(f || '').toLowerCase()).filter(Boolean);
    let total = 0;
    for (const t of terms) {
      let best = 0;
      for (const v of vals) {
        if (v === t) best = Math.max(best, 100);
        else if (v.startsWith(t)) best = Math.max(best, 60);
        else if (v.split(/\s+/).some((w) => w.startsWith(t))) best = Math.max(best, 45);
        else if (v.includes(t)) best = Math.max(best, 30);
      }
      if (!best) return 0;
      total += best;
    }
    return total / terms.length;
  }
  const looksLikeQuestion = (q) => q.endsWith('?') || q.split(/\s+/).length >= 2
    || /^(how|what|when|where|which|why|can|does|do|is|are|should|min|max|minimum|maximum)\b/.test(q);

  function runFind() {
    const q = state.q.trim().toLowerCase();
    const d = state.data;
    if (!q || !d) { state.results = []; return; }
    const found = [];
    for (const [id, label] of SCREENS) { const s = score(q, label, id); if (s) found.push({ s: s + 5, type: 'screen', id, title: label, subType: 'Screen', dot: 'var(--brand-grey)' }); }
    for (const u of d.users) {
      if (u.active === false) continue;
      const s = score(q, u.displayName, u.id, u.email, ...(u.roles || []));
      if (s) found.push({ s: s + 4, type: 'person', id: u.id, title: titleCase(u.displayName || u.id), subType: 'Person', dot: 'var(--brand-grey)' });
    }
    for (const p of d.projects) {
      const s = score(q, p.number, p.clientName, p.title, p.status, p.service, p.scheme, p.siteAddress && p.siteAddress.postcode);
      if (s) found.push({ s: s + 3, type: 'project', id: p.id, ref: p.number || '', title: p.clientName || p.title || '—', subType: 'Project', dot: 'var(--brand-orange)' });
    }
    for (const c of d.clients) {
      const s = score(q, c.name, c.contactName, ...(Array.isArray(c.emails) ? c.emails : []));
      if (s) found.push({ s: s + 2, type: 'client', id: c.id, title: c.name || '—', subType: 'Client', dot: 'var(--brand-grey)' });
    }
    for (const sp of d.suppliers) {
      const s = score(q, sp.name, sp.contactName, sp.category);
      if (s) found.push({ s: s + 2, type: 'supplier', id: sp.id, title: sp.name || '—', subType: 'Supplier', dot: 'var(--brand-grey)' });
    }
    for (const e of d.enquiries) {
      const name = e.clientRef ? d.clientName[refTail(e.clientRef)] : (e.clientName || '');
      const s = score(q, e.number, name, e.description);
      if (s) found.push({ s: s + 1, type: 'enquiry', id: e.id, ref: e.number || '', title: name || (e.status || '—'), subType: 'Enquiry', dot: 'var(--brand-blue)' });
    }
    for (const iv of d.invoices) {
      const name = iv.clientRef ? d.clientName[refTail(iv.clientRef)] : '';
      const s = score(q, iv.number, name);
      if (s) found.push({ s, type: 'invoice', id: iv.id, ref: String(iv.number || ''), number: iv.number, title: name || '—', subType: `Invoice · ${money(Number(iv.amount) || 0)}`, dot: 'var(--brand-blue)' });
    }
    const pad2 = (n) => String(n).padStart(2, '0');
    for (const ins of (d.instructions || [])) {
      const p = d.projById[ins.projectId];
      const pref = p ? `${p.number}-${pad2(Number(ins.seq) || 1)}` : '';
      const s = score(q, pref, ins.description, p && p.number, p && p.clientName);
      if (s) found.push({ s: s + 1, type: 'instruction', id: ins.projectId, projectId: ins.projectId, ref: pref, title: ins.description || 'Original scope', subType: 'Instruction', dot: 'var(--brand-blue)' });
    }
    for (const f of (d.files || [])) {
      const s = score(q, f.docNumber, f.filename, f.projectNo);
      if (s) found.push({ s, type: 'file', id: f.id, projectNo: f.projectNo, ref: f.docNumber || String(f.projectNo || ''), title: f.filename || '—', subType: 'File', dot: 'var(--brand-grey)' });
    }
    // Settings (stage 1, the people on settings.features.settingsV2): the same search as the
    // Settings head box, over only the rows this person may change. Opens the row in Settings.
    if (!isContractor() && settingsV2On({ email, features: orgSetting('features', {}) })) {
      const rows = visibleRows({ tier: callerTier(session.roles), template: '', hasFeature, gates: { financeFy27: financeFy27(email) } });
      for (const x of searchSettings(q, rows, { settings: orgSettings() || {}, org: { name: orgInfo().name } }, { limit: 4 }).hits) {
        found.push({ s: x.score * 0.9 + 4, type: 'setting', id: x.id, ref: x.value ? String(x.value).slice(0, 28) : '', title: x.label, subType: `Setting · ${x.path}`, dot: 'var(--brand-grey)' });
      }
    }
    state.results = found.sort((a, b) => b.s - a.s).slice(0, 7);
    state.sel = 0;
    if (canWhatsApp(email)) findWhatsApp(q);
    // Assets mission 4: "who has the Disto" — the asset rows of the index (assetSearch).
    const assetQ = canAssets() && /^(who|where)(’s|'s)?\b/.test(q);
    if (canAssets()) findAssets(q);

    // The mode switches itself (§8): no record, reads like a standards
    // question → Ask Track, words intact, and the footer says why.
    // "Who has / where is …" stays in Find while assets answer it.
    if (!state.results.length && q.length >= 6 && looksLikeQuestion(q) && state.mode === 'find' && !assetQ) {
      state.mode = 'ask';
      state.autoswitched = { from: 'find', reason: 'No record matches, so this is now a question.' };
      sessionStorage.setItem(MODE_KEY, 'ask');
    }
  }

  // WhatsApp is searchable too (17/10/2026, the AI build's waSearch): asked
  // behind the records, the hits join the list when they arrive and open the
  // project they are filed to. A failure, or no hits, changes nothing.
  let waTimer = null;
  function findWhatsApp(q) {
    clearTimeout(waTimer);
    if (q.length < 3) return;
    waTimer = setTimeout(async () => {
      let hits = [];
      try { hits = ((await waSearch({ query: q })) || {}).hits || []; } catch (_) { return; }
      if (state.mode !== 'find' || state.q.trim().toLowerCase() !== q) return;
      const extra = hits.filter((h) => h && h.projectId).slice(0, 3).map((h) => ({
        s: 0, type: 'project', id: h.projectId, ref: h.projectNo || '',
        title: String(h.snippet || '').replace(/\s+/g, ' ').slice(0, 90) || 'WhatsApp message',
        subType: `WhatsApp${h.from ? ` · ${typeof h.from === 'string' ? h.from : (h.from.name || '')}` : ''}`, dot: 'var(--brand-grey)',
      }));
      if (!extra.length) return;
      state.results = state.results.filter((r) => !String(r.subType || '').startsWith('WhatsApp')).concat(extra).slice(0, 10);
      repaintKeepingField();
    }, 350);
  }

  // Assets (mission 4): asked behind the records like WhatsApp; a hit says who has it
  // and opens the asset. Read-only (Tier 1). A failure, or no hits, changes nothing.
  let assetTimer = null;
  function findAssets(q) {
    clearTimeout(assetTimer);
    if (q.length < 3) return;
    assetTimer = setTimeout(async () => {
      let hits = [];
      try { const { callFunction } = await import('./db.js'); hits = ((await callFunction('assetSearch', { query: q, limit: 3 })) || {}).hits || []; } catch (_) { return; }
      if (state.mode !== 'find' || state.q.trim().toLowerCase() !== q) return;
      const extra = hits.filter((h) => h && h.number).slice(0, 3).map((h) => ({
        s: 0, type: 'asset', id: h.assetId || h.number, ref: String(h.number),
        title: String(h.name || 'Asset').slice(0, 90),
        subType: `Asset · ${h.holderName ? `${h.holderName} has it` : 'nobody has it out'}`, dot: 'var(--brand-grey)',
      }));
      if (!extra.length) return;
      state.results = state.results.filter((r) => r.type !== 'asset').concat(extra).slice(0, 10);
      repaintKeepingField();
    }, 350);
  }

  // ── note targeting (21a/21b): typed reference → context → refuse. A typed
  // "8415-2" or "8415 #2" aims at that INSTRUCTION (Karan 08/08: build it);
  // multi-instruction projects also offer their instructions to narrow to. ──
  function noteTarget() {
    const text = state.q;
    const d = state.data;
    if (d) {
      const withSeq = text.match(/\b(\d{4,5})\s*(?:-|#|\s#)\s*0?(\d{1,2})\b/);
      if (withSeq) {
        const p = d.projects.find((x) => String(x.number) === withSeq[1]);
        if (p) return { project: p, seq: Number(withSeq[2]), how: 'from the reference you typed', strip: withSeq[0] };
      }
      const num = (text.match(/\b(\d{4,5})\b/) || [])[1];
      if (num) {
        const p = d.projects.find((x) => String(x.number) === num);
        if (p) return { project: p, how: 'from the number you typed', strip: num };
      }
      // A recognisable client name anywhere in the note wins too.
      const low = ` ${text.toLowerCase()} `;
      for (const p of d.projects) {
        const name = String(p.clientName || '').toLowerCase().trim();
        if (name.length >= 4 && low.includes(` ${name} `)) return { project: p, how: 'from the name you typed', strip: p.clientName };
      }
    }
    if (context.project && d) {
      const p = d.projects.find((x) => x.id === context.project.id || String(x.number) === String(context.project.number));
      if (p) return { project: p, how: 'you were here', strip: null };
    }
    return null;
  }

  // Instructions of the target project (lazy) — offered when there's more than
  // one, so a note can land on the instruction rather than the project.
  async function ensureTargetInstructions() {
    const t = state.noteTarget;
    if (!t || t.seq || t.instructions) return;
    try {
      const list = await listInstructions(t.project.id);
      if (state.noteTarget === t) { t.instructions = list.length > 1 ? list : []; repaintKeepingField(); }
    } catch (_) { t.instructions = []; }
  }

  // ── @mentions: "@sanj" anywhere in the note notifies that person (email from
  // track@, internal addresses only — the function enforces it too). The @name
  // stays in the text; matching is by first name or account id. ──
  function noteMentions() {
    const d = state.data;
    if (!d) return [];
    const out = [];
    for (const m of state.q.matchAll(/@([a-z0-9._-]{2,})/gi)) {
      const tok = m[1].toLowerCase();
      const u = d.users.find((x) => x.active !== false && isPerson(x) && (
        String(x.id || '').toLowerCase() === tok
        || String(x.displayName || '').toLowerCase().split(/\s+/)[0] === tok
        || String(x.email || '').toLowerCase().split('@')[0] === tok));
      if (u && u.email && !out.some((o) => o.email === u.email)) out.push({ email: u.email, name: titleCase(u.displayName || u.id) });
    }
    return out.filter((o) => String(o.email).toLowerCase() !== email);
  }

  async function saveNote(target) {
    let text = state.q.trim();
    if (target.strip) {
      // The reference becomes the target, not the first word (21a state 03).
      const re = new RegExp(String(target.strip).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      text = text.replace(re, '').replace(/\s{2,}/g, ' ').replace(/^[\s,:·-]+/, '').trim();
      text = text.charAt(0).toUpperCase() + text.slice(1);
    }
    if (!text) return;
    const fromScreen = SCREEN_LABEL[context.screen] || titleCase(context.screen || '');
    const mentions = noteMentions();
    let ref;
    try {
      ref = await addNote({
        text, userName: myName, userEmail: session.email,
        projectNo: String(target.project.number || ''), projectId: target.project.id,
        ...(target.seq ? { instructionSeq: Number(target.seq) } : {}),
        ...(mentions.length ? { mentions: mentions.map((m) => m.email) } : {}),
        addedFrom: fromScreen, via: 'ask-track', createdAt: new Date().toISOString(),
      });
    } catch (e) {
      // Nothing in this collection after five weeks (audit 12/09) and no way to
      // tell whether it was never used or never worked — because a rejected
      // write landed in an unhandled promise: the panel stayed open, the words
      // stayed put, and the writer was told nothing at all. Now it says.
      state.noteError = e && e.message ? e.message : String(e);
      console.error('[ask] note not saved:', state.noteError);
      repaintKeepingField();
      return;
    }
    state.noteError = null;
    state.q = ''; state.noteTarget = null;
    close();
    showSavedCard({ id: ref.id, text, project: target.project, seq: target.seq, mentions });
  }

  function showSavedCard({ id, text, project, seq, mentions = [] }) {
    const two = (n) => String(n).padStart(2, '0');
    const now = new Date();
    toastHost.innerHTML = `
      <div style="width:340px;background:var(--brand-white);border:1px solid var(--brand-grey-200);border-radius:14px;padding:15px 17px;display:flex;flex-direction:column;gap:10px;box-shadow:0 18px 44px rgba(12,17,57,0.18);animation:bm-rise 200ms ${EASE} both;">
        <span style="font-size:12px;font-weight:500;color:var(--brand-orange);">Noted on ${escapeHtml(`${project.number || ''} ${project.clientName || project.title || ''}`.trim())}${seq ? ` · Instruction ${seq}` : ''}</span>
        <span style="font-size:13px;line-height:1.5;color:var(--brand-grey-700);">${escapeHtml(text)}</span>
        ${mentions.length ? `<span style="font-size:11.5px;color:var(--brand-grey-500);">Notifying ${escapeHtml(mentions.map((m) => m.name).join(', '))}</span>` : ''}
        <div style="display:flex;align-items:center;gap:16px;padding-top:4px;border-top:1px solid var(--brand-grey-200);">
          <button class="askbar-undo" style="font-size:12px;color:var(--brand-blue);background:none;border:0;padding:0;cursor:pointer;">Undo</button>
          <button class="askbar-goto" style="font-size:12px;color:var(--brand-blue);background:none;border:0;padding:0;cursor:pointer;">Open the project</button>
          <div style="flex:1;"></div>
          <span style="font-family:var(--font-technical);font-size:11px;color:var(--brand-grey-500);">${two(now.getHours())}:${two(now.getMinutes())}</span>
        </div>
      </div>`;
    const clear = () => { toastHost.innerHTML = ''; };
    const timer = setTimeout(clear, 6000);
    toastHost.querySelector('.askbar-undo').addEventListener('click', async () => {
      clearTimeout(timer); clear();
      try { await deleteDoc(fsDoc(fsDb, `organisations/${ORG_ID}/notes/${id}`)); } catch (_) { /* already gone */ }
    });
    toastHost.querySelector('.askbar-goto').addEventListener('click', () => {
      clearTimeout(timer); clear();
      open({ type: 'project', id: project.id });
    });
  }

  // ── rendering ──
  const rowHtml = (r, i, extra = '') => `
    <div class="askbar-row" data-i="${i}" style="padding:10px 18px;display:flex;align-items:center;gap:13px;cursor:pointer;${i === state.sel ? 'background:var(--ab-sel);' : ''}">
      <span style="width:7px;height:7px;border-radius:50%;background:${r.dot || 'var(--brand-grey)'};flex:0 0 7px;"></span>
      ${r.ref ? `<span style="font-family:var(--font-technical);font-size:12px;color:var(--ab-ink-3);">${escapeHtml(r.ref)}</span>` : ''}
      <span style="flex:1;min-width:0;font-size:13.5px;color:var(--ab-ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(r.title)}</span>
      <span style="font-size:11px;color:var(--ab-ink-3);">${escapeHtml(extra || r.subType || '')}</span>
      ${i === state.sel && i >= 0 ? '<span style="font-family:var(--font-technical);font-size:11px;color:var(--ab-ink-3);">&#8629;</span>' : ''}
    </div>`;


  // ── Task mode (design_handoff_tasks_module, artboard 1a) ──────────────────
  // Quick-add lives HERE rather than in its own 680px overlay: ⌘K already opens
  // this panel, and two overlays fighting over one chord is worse than one
  // panel with a fourth tab (Karan 28/08). What carries over from artboard 1a
  // is the behaviour and the information design — the parse, "Reads as", the
  // chip order, the backlog note, and a picker instead of a guess. What does
  // not is the colourway: 1a is drawn white-on-white and this panel is navy, so
  // the chips are restated in the bar's own idiom rather than pasted onto it.
  // The 1a-faithful overlay still exists in views/quick-add.js for the mobile
  // bottom sheet (1f) and the project tab's pre-filled add (1c).

  // The parser wants {id,name,ref,number,postcode,sector,state}; Track projects
  // and users are shaped differently, so adapt rather than teach it our schema.
  const taskProjects = () => (state.data?.projects || []).map((p) => ({
    id: p.id,
    name: p.clientName || p.title || '',
    ref: p.number || '', number: p.number || '',
    postcode: (p.siteAddress && p.siteAddress.postcode) || '',
    sector: p.scheme || p.service || '', state: p.status || '',
  })).filter((p) => p.name);
  const taskPeople = () => (state.data?.users || [])
    .filter((u) => u.active !== false && isPerson(u))
    .map((u) => ({ id: u.id, name: titleCase(u.displayName || u.id), email: u.email }));

  function taskParse() {
    const parsed = parseTaskLine(state.q, { projects: taskProjects(), people: taskPeople() });
    // A project page pre-fills its own chip, as the handoff asks — but only
    // when the line has not named one itself.
    if (!parsed.project && !parsed.ambiguous && context.project && !state.taskUnlinked) {
      const c = context.project;
      parsed.project = { id: c.id, name: c.clientName || c.title || c.number || '', ref: c.number || '', fromContext: true };
    }
    return parsed;
  }

  function taskChipHtml(c) {
    const label = `<span style="font-family:var(--font-technical);font-size:9.5px;letter-spacing:0.10em;color:var(--ab-ink-3);">${escapeHtml(c.label)}</span>`;
    if (c.empty) {
      return `<span style="border:1px dashed var(--ab-hair);border-radius:8px;padding:7px 11px;display:flex;align-items:center;gap:9px;">
        ${label}<span style="font-size:13px;color:var(--ab-ink-3);">${escapeHtml(c.value)}</span></span>`;
    }
    const dot = c.dot
      ? `<span style="width:7px;height:7px;border-radius:50%;flex:none;${c.dot === 1 ? 'background:var(--brand-orange);' : c.dot === 2 ? 'background:var(--ab-ink-3);' : 'border:1.5px solid var(--ab-ink-3);'}"></span>` : '';
    return `<span style="border:1px solid var(--ab-hair);background:var(--ab-sel);border-radius:8px;padding:7px 8px 7px 11px;display:flex;align-items:center;gap:9px;">
      ${label}${dot}<span style="font-size:13px;color:var(--ab-ink);">${escapeHtml(c.value)}</span>
      <button class="askbar-chip-x" data-drop="${escapeHtml(c.kind)}" aria-label="Remove ${escapeHtml(c.label.toLowerCase())}" style="font-size:13px;color:var(--ab-ink-3);background:none;border:0;padding:0;cursor:pointer;font-family:inherit;line-height:1;">&times;</button></span>`;
  }

  function taskBodyHtml() {
    const q = state.q.trim();
    if (!q) {
      return `<div style="padding:18px;display:flex;flex-direction:column;gap:10px;">
        <span style="font-size:12.5px;line-height:1.55;color:var(--ab-ink-2);">Type naturally — <span style="font-family:var(--font-technical);">fri</span>, <span style="font-family:var(--font-technical);">p1</span>, <span style="font-family:var(--font-technical);">@name</span>, <span style="font-family:var(--font-technical);">2h</span> and any project name are read out of the line.</span>
        <span style="font-size:11.5px;color:var(--ab-ink-3);">A task gets a due date here. The day you commit to it is yours to choose in the planner.</span>
      </div>`;
    }
    const p = taskParse();
    if (p.ambiguous) {
      const rows = p.ambiguous.matches.map((m, i) => `
        <div class="askbar-tpick" data-i="${i}" style="padding:11px 18px;display:flex;align-items:center;gap:12px;cursor:pointer;${i === state.taskPick ? 'background:var(--ab-sel);' : ''}">
          <span style="font-family:var(--font-technical);font-size:11px;color:var(--ab-ink-3);width:62px;flex:none;">${escapeHtml(m.ref || m.number || '—')}</span>
          <span style="flex:1;min-width:0;font-size:14px;color:var(--ab-ink);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(m.name)}</span>
          <span style="font-size:12.5px;color:var(--ab-ink-3);white-space:nowrap;">${escapeHtml([m.sector, m.state].filter(Boolean).join(' · '))}</span>
          ${i === state.taskPick ? '<span style="font-family:var(--font-technical);font-size:10.5px;color:var(--brand-orange);">&#8629;</span>' : ''}
        </div>`).join('');
      const n = p.ambiguous.matches.length;
      return `<div style="padding:14px 18px 10px;display:flex;align-items:center;gap:10px;">
          <span style="width:6px;height:6px;border-radius:50%;background:var(--brand-orange);flex:none;"></span>
          <span style="font-size:12.5px;line-height:1.5;color:var(--ab-ink-2);">${n === 2 ? 'Two' : n} projects match &ldquo;${escapeHtml(p.ambiguous.token)}&rdquo;. Pick one, or leave it unlinked.</span>
        </div>${rows}
        <div class="askbar-tpick" data-i="${n}" style="padding:11px 18px;display:flex;align-items:center;gap:12px;cursor:pointer;${state.taskPick === n ? 'background:var(--ab-sel);' : ''}">
          <span style="font-family:var(--font-technical);font-size:11px;color:var(--ab-ink-4);width:62px;flex:none;">—</span>
          <span style="flex:1;font-size:14px;color:var(--ab-ink-2);">Leave unlinked</span>
          <span style="font-size:12.5px;color:var(--ab-ink-3);">no timer, no project tab</span>
        </div>`;
    }
    const owner = p.owner ? String(p.owner.name || p.owner.id).split(' ')[0] : null;
    return `<div style="padding:14px 18px 16px;display:flex;flex-direction:column;gap:12px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <span style="font-family:var(--font-technical);font-size:12px;color:var(--ab-ink-3);">Reads as</span>
        <span style="font-size:14.5px;font-weight:500;color:var(--ab-ink);">${escapeHtml(p.title || (p.project ? p.project.name : '—'))}</span>
      </div>
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">${chipsOf(p).map(taskChipHtml).join('')}</div>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
        <span style="width:6px;height:6px;border-radius:50%;background:var(--brand-orange);flex:none;"></span>
        <span style="font-size:12.5px;line-height:1.5;color:var(--ab-ink-2);">No planned date — this goes to ${owner ? escapeHtml(owner) + '&rsquo;s backlog' : 'the backlog'}, not ${owner ? 'their' : 'a'} Today.</span>
        ${p.dueDate ? `<button class="askbar-tplan" style="font-size:12.5px;color:var(--brand-orange);background:none;border:0;padding:0;cursor:pointer;font-family:inherit;">Plan it for ${escapeHtml(dueLabel(p.dueDate))}</button>` : ''}
      </div>
    </div>`;
  }

  async function saveTask({ plan = false } = {}) {
    const p = taskParse();
    if (!p.title.trim() && !p.project) return;
    const me = (state.data?.users || []).find((u) => String(u.email).toLowerCase() === email);
    try {
      const t = await createTask({
        title: p.title || (p.project ? p.project.name : ''),
        projectId: p.project?.id || null,
        dueDate: p.dueDate,
        priority: p.priority || 2,
        estimateHours: p.estimateHours,
        // Unassigned is legal — that is the team-meeting pool. But a task you
        // type for yourself should land on you, not in it.
        assigneeId: p.owner?.id || (me ? me.id : null),
        assigneeEmail: p.owner?.email || (me ? me.email : null),
      }, { email, displayName: session?.displayName });
      if (plan && p.dueDate) await planTask(t.id, p.dueDate, { email });
      state.q = ''; state.taskPick = 0; state.taskUnlinked = false;
      paint();
      showTaskSaved(t, plan && p.dueDate ? p.dueDate : null);
      // Anything added while Focus is open is an INTERRUPTION CAPTURED — the
      // Tasks page counts it in the corner and reviews it at the close, which
      // is what lets a phone call become a task instead of a derailment.
      document.dispatchEvent(new CustomEvent('track:task-added', { detail: { id: t.id, title: t.title } }));
    } catch (e) {
      state.taskError = e?.message || 'Could not save that task.';
      paint();
    }
  }

  function showTaskSaved(t, planned) {
    toastHost.innerHTML = `
      <div style="background:var(--brand-navy);color:var(--brand-white);border-radius:12px;padding:13px 16px;display:flex;align-items:center;gap:14px;box-shadow:0 14px 34px rgba(12,17,57,0.30);">
        <span style="width:7px;height:7px;border-radius:50%;background:var(--brand-orange);flex:none;"></span>
        <span style="flex:1;min-width:0;font-size:13px;">${escapeHtml(t.title)}${planned ? ' — planned for ' + escapeHtml(dueLabel(planned)) : ' — in your backlog'}</span>
      </div>`;
    setTimeout(() => { toastHost.innerHTML = ''; }, 4200);
  }

  // state.q is the truth, but repaintKeepingField() deliberately does not touch
  // the field (it exists to preserve what someone is typing). When the CODE
  // changes the line, the field has to be written back or the two disagree.
  function setLine(v) {
    state.q = v;
    const f = surface.querySelector('.askbar-field');
    if (f) f.value = v;
    repaintKeepingField();
  }

  // Removing a chip edits the line, or the next keystroke parses it straight
  // back. Dropping the project only unlinks it — the words stay, because they
  // are usually part of what the task says.
  function dropChip(kind) {
    if (kind === 'project') { state.taskUnlinked = true; repaintKeepingField(); return; }
    const span = (taskParse().spans || []).find((x) => x.kind === kind);
    if (!span) return;
    setLine((state.q.slice(0, span.start) + state.q.slice(span.end)).replace(/\s{2,}/g, ' ').replace(/\s+$/, ''));
  }

  // Picking rewrites the LINE, so the field stays the one source of what the
  // task says — the same contract as the standalone overlay.
  function taskChoose() {
    const p = taskParse();
    if (!p.ambiguous) return;
    const m = p.ambiguous.matches[state.taskPick] || null;
    const tok = p.ambiguous.token;
    const at = state.q.toLowerCase().lastIndexOf(tok.toLowerCase());
    if (!m) state.taskUnlinked = true;
    state.taskPick = 0;
    if (at >= 0) setLine((state.q.slice(0, at) + (m ? m.name : '') + state.q.slice(at + tok.length)).replace(/\s+$/, '') + ' ');
    else repaintKeepingField();
  }

  /**
   * THE ANSWER, IN THE PANEL (§7).
   *
   * Prose, then a FIGURES TABLE — label, value in mono, source in mono on the
   * right — then a line saying what it read. Figures never sit buried in a
   * sentence; that is the answer contract in handoff-ask-track/BRIEF.md and the
   * reason the model returns them separately at all.
   *
   * This reads the SAME `{ answer, figures, citations, notes }` the Library
   * screen renders. It is a second rendering rather than a shared one because
   * the two grounds are different — the Library is ink on paper with room for
   * locators and an open-in-Drive link; this is 420px of navy. What must not
   * drift is the CONTRACT, and test/ask-answer.test.mjs holds both to it.
   */
  function askAnswerHtml() {
    const a = state.ask;
    if (!a) return '';
    if (a.asking) {
      return `<div style="padding:18px;display:flex;align-items:center;gap:10px;">
        <span style="width:6px;height:6px;border-radius:50%;background:var(--brand-orange);animation:creatingPulse 1.2s linear infinite;"></span>
        <span style="font-size:13px;color:var(--ab-ink-2);">Reading the approved library…</span></div>`;
    }
    if (a.error) {
      return `<div style="padding:18px;font-size:13px;line-height:1.55;color:var(--ab-ink-2);">${escapeHtml(a.error)}</div>`;
    }
    const out = a.out || {};
    const cites = out.citations || [];
    // Page numbers live in c.pages (a list); there was never a single page field, so
    // the panel showed no page at all until 11/10/2026 (spec 05 M).
    const locator = (i) => {
      const c = cites[i];
      if (!c) return '';
      const pages = Array.isArray(c.pages) ? c.pages : [];
      const where = pages.length > 1 ? `pp.${pages.join(', ')}` : pages.length ? `p.${pages[0]}` : null;
      const tag = c.status === 'withdrawn' ? `WITHDRAWN${c.supersededBy ? ` — superseded by ${c.supersededBy}` : ''}` : null;
      return escapeHtml([c.file || c.doc, where, tag].filter(Boolean).join(' · '));
    };
    const rowsHtml = (figuresIn, notes) => {
      const figures = (figuresIn && figuresIn.rows && figuresIn.rows.length) ? figuresIn : null;
      // A short value (a figure) sits right of its label; a long one (a quoted
      // requirement) goes under it and wraps. Kept on one line, a quote took the
      // whole width and crushed the label into it (Karan 15/10, AD H5).
      const figRows = figures ? figures.rows.map((r) => {
        const long = String(r.value == null ? '' : r.value).length > 28;
        return `
      <div style="display:grid;grid-template-columns:${long ? 'minmax(0,1fr)' : 'minmax(0,1fr) auto'};gap:${long ? '4px' : '6px 14px'};padding:10px 18px;border-top:1px solid ${HAIR};">
        <span style="font-size:${long ? '11px;letter-spacing:0.04em;color:var(--ab-ink-3)' : '13px;color:var(--ab-ink)'};line-height:1.45;min-width:0;">${escapeHtml(r.label)}</span>
        <span style="${long ? 'font-size:13px;line-height:1.5;overflow-wrap:anywhere;' : 'font-family:var(--font-technical);font-size:13.5px;font-weight:500;text-align:right;white-space:nowrap;'}color:var(--ab-ink);min-width:0;">${escapeHtml(r.value)}</span>
        ${locator(r.cite) ? `<span style="grid-column:1/-1;font-family:var(--font-technical);font-size:10.5px;color:var(--ab-ink-3);">${locator(r.cite)}</span>` : ''}
      </div>`;
      }).join('') : '';
      const noteRows = (notes || []).map((n) => `
      <div style="padding:10px 18px;border-top:1px solid ${HAIR};">
        <span style="font-size:12.5px;line-height:1.55;color:var(--ab-ink-2);">${escapeHtml(n.text)}</span>
        ${locator(n.cite) ? `<span style="display:block;margin-top:4px;font-family:var(--font-technical);font-size:10.5px;color:var(--ab-ink-3);">${locator(n.cite)}</span>` : ''}
      </div>`).join('');
      return figRows + noteRows;
    };
    const docs = new Set(cites.map((c) => c.doc).filter(Boolean)).size;
    const escalated = out.status === 'escalated';
    // The opening sentence: `answer` only exists on limited/escalated replies;
    // an answered one carries `lead` — which the panel used to hide.
    const opening = out.lead || out.answer;
    const parts = Array.isArray(out.parts) ? out.parts : [];
    const EYE = 'display:block;padding:12px 18px 4px;font-size:12px;font-weight:500;';
    const body = parts.length > 1
      ? parts.map((p, i) => `
        <span style="${EYE}color:var(--ab-ink-3);">Part ${i + 1}${p.part ? ` · ${escapeHtml(p.part)}` : ''}</span>
        ${p.status === 'not_in_library'
    ? `<p style="margin:0;padding:4px 18px 12px;font-size:13px;line-height:1.5;color:var(--ab-ink-2);">Not in your library — ${escapeHtml(p.gap || 'no approved source covers this part.')}</p>`
    : `${p.lead ? `<p style="margin:0;padding:4px 18px 12px;font-size:13.5px;line-height:1.5;color:var(--ab-ink);">${escapeHtml(p.lead)}</p>` : ''}${rowsHtml(p.figures, p.notes)}`}`).join('')
      : `${opening ? `<p style="margin:0;padding:16px 18px 14px;font-size:14px;line-height:1.5;color:var(--ab-ink);">${escapeHtml(opening)}</p>` : ''}${rowsHtml(out.figures, out.notes)}`;
    // Judgement may use general engineering knowledge, so it is never shown
    // without its label (Karan, 11/10/2026).
    const judgement = out.judgement ? `
      <div style="padding:12px 18px;border-top:1px solid ${HAIR};">
        <span style="${EYE.replace('padding:12px 18px 4px;', 'padding:0 0 4px;')}color:var(--brand-orange);">Engineering judgement — not from your library</span>
        <span style="font-size:12.5px;line-height:1.55;color:var(--ab-ink-2);">${escapeHtml(out.judgement)}</span>
      </div>` : '';
    return `<div style="display:flex;flex-direction:column;">
      ${out.degraded ? `<span style="padding:12px 18px 0;font-size:11.5px;color:var(--ab-ink-3);">Search ran in reduced mode — answer may be incomplete.</span>` : ''}
      ${body}${judgement}
      <div style="display:flex;align-items:center;gap:9px;padding:12px 18px;border-top:1px solid ${HAIR};">
        <span style="width:6px;height:6px;border-radius:50%;background:${escalated ? 'var(--brand-orange)' : 'var(--status-ok)'};flex:0 0 6px;"></span>
        <span style="font-size:11.5px;color:var(--ab-ink-3);">${escalated
          ? 'No approved source &#183; escalated with a reference'
          : `${escapeHtml(String(docs))} approved source${docs === 1 ? '' : 's'} &#183; never guesses`}</span>
        <div style="flex:1;"></div>
        <button class="askbar-ask-full" style="background:none;border:0;padding:0;cursor:pointer;font-size:11.5px;color:var(--ab-ink-2);text-decoration:underline;text-underline-offset:3px;">Open in full</button>
      </div>
    </div>`;
  }

  function bodyHtml() {
    const q = state.q.trim();
    if (state.mode === 'task') return taskBodyHtml();
    if (state.mode === 'ask') {
      if (state.ask) return askAnswerHtml();
      if (state.autoswitched) {
        return `<div style="padding:22px 18px;display:flex;flex-direction:column;gap:8px;">
          <span style="font-size:13.5px;color:var(--ab-ink);">${escapeHtml(state.autoswitched.reason)}</span>
          <span style="font-size:12.5px;line-height:1.55;color:var(--ab-ink-3);">${escapeHtml(q ? `“${q}” reads like a standards question, and nothing in the records matched it.` : '')}</span>
        </div>`;
      }
      return `<div style="padding:18px;display:flex;flex-direction:column;gap:12px;">
        <span style="font-size:12.5px;line-height:1.55;color:var(--ab-ink-2);">Answered from the approved register only, and cited. Judgement stays with you.</span>
        <div style="display:flex;align-items:center;gap:12px;">
          <span style="display:flex;align-items:center;gap:8px;font-size:12px;color:var(--ab-ink-3);"><span style="width:6px;height:6px;border-radius:50%;background:var(--status-ok);"></span>${state.sourceCount == null ? '…' : `${state.sourceCount} sources`}</span>
          <div style="flex:1;"></div>
          <button class="askbar-ask-go" style="background:var(--ab-btn);color:var(--ab-btn-ink);border:0;border-radius:8px;padding:9px 18px;font-size:12.5px;font-weight:500;cursor:pointer;">Ask</button>
        </div>
      </div>`;
    }
    if (state.mode === 'note') {
      const target = state.noteTarget;
      const mentions = noteMentions();
      const mentionLine = mentions.length
        ? `<div style="padding:8px 18px 4px;font-size:11.5px;color:var(--ab-ink-3);">Will notify ${escapeHtml(mentions.map((m) => m.name).join(', '))} — from track@, on save.</div>` : '';
      if (target) {
        const label = `${target.project.number || ''} ${target.project.clientName || target.project.title || ''}`.trim()
          + (target.seq ? ` · Instruction ${target.seq}` : '');
        const instrRows = (!target.seq && target.instructions && target.instructions.length)
          ? target.instructions.map((ins) => `
            <div class="askbar-instr" data-seq="${escapeHtml(String(ins.seq))}" style="padding:8px 18px 8px 38px;display:flex;align-items:center;gap:13px;cursor:pointer;">
              <span style="font-family:var(--font-technical);font-size:12px;color:var(--ab-ink-3);">#${escapeHtml(String(ins.seq))}</span>
              <span style="flex:1;min-width:0;font-size:12.5px;color:var(--ab-ink-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(ins.description || 'Original scope')}</span>
              <span style="font-size:10.5px;color:var(--ab-ink-3);">aim here</span>
            </div>`).join('')
          : '';
        return `<div style="padding:8px 0 6px;display:flex;flex-direction:column;">
          <div style="padding:6px 18px 8px;font-size:12px;color:var(--ab-ink-3);">Lands on</div>
          ${rowHtml({ dot: 'var(--brand-orange)', title: label }, 0, target.how)}
          ${instrRows}
          ${context.project && target.how !== 'you were here' && state.data ? (() => {
            const cp = state.data.projects.find((x) => x.id === context.project.id);
            return cp && cp.id !== target.project.id ? rowHtml({ dot: 'var(--brand-grey)', title: `${cp.number || ''} ${cp.clientName || cp.title || ''}`.trim() }, 1, 'you were here') : '';
          })() : ''}
          ${mentionLine}
        </div>`;
      }
      const nearby = state.weekProjects || [];
      return `<div style="padding:8px 0 6px;display:flex;flex-direction:column;">
        <div style="padding:6px 18px 8px;font-size:12px;color:var(--brand-orange);">Needs a project</div>
        ${rowHtml({ dot: 'var(--brand-orange)', title: 'Pick one, or type a number or a name' }, -1)}
        ${nearby.map((p, i) => rowHtml({ dot: 'var(--brand-grey)', title: `${p.number || ''} ${p.clientName || p.title || ''}`.trim(), id: p.id }, i, 'logged this week')).join('')}
        ${mentionLine}
      </div>`;
    }
    // find
    if (!q) {
      const rs = recents();
      return `<div style="padding:8px 0 6px;display:flex;flex-direction:column;">
        ${rs.length ? `<div style="padding:6px 18px 8px;font-size:12px;color:var(--ab-ink-3);">Recent</div>` : ''}
        ${rs.map((r, i) => rowHtml({ ...r, dot: r.type === 'project' ? 'var(--brand-orange)' : r.type === 'enquiry' || r.type === 'invoice' ? 'var(--brand-blue)' : 'var(--brand-grey)', subType: r.sub }, i)).join('')}
      </div>`;
    }
    if (!state.data) return `<div style="padding:18px;font-size:12.5px;color:var(--ab-ink-3);">${state.loading ? 'Loading the search index…' : ''}</div>`;
    if (!state.results.length) {
      return `<div style="padding:8px 0 6px;">
        <div style="padding:10px 18px;display:flex;align-items:center;gap:13px;">
          <span style="width:7px;height:7px;border-radius:50%;background:var(--brand-orange);flex:0 0 7px;"></span>
          <span style="flex:1;min-width:0;font-size:13.5px;color:var(--ab-ink-2);">No record matches &ldquo;${escapeHtml(q)}&rdquo;</span>
          <span style="font-size:11px;color:var(--ab-ink-3);">try Ask Track</span>
        </div>
      </div>`;
    }
    return `<div style="padding:8px 0 6px;display:flex;flex-direction:column;overflow-y:auto;">
      <div style="padding:6px 18px 8px;font-size:12px;color:var(--ab-ink-3);">Matches</div>
      ${state.results.map((r, i) => rowHtml(r, i)).join('')}</div>`;
  }

  function footerHtml() {
    const q = state.q.trim();
    if (state.mode === 'task') {
      if (state.taskError) return escapeHtml(state.taskError);
      if (!q) return 'A task can name a project, a day, a priority, an owner and a rough length — all in the one line.';
      const p = taskParse();
      if (p.ambiguous) return '<span style="font-family:var(--font-technical);">&#8629;</span> takes the highlighted project. Arrow keys move; the last row leaves it unlinked.';
      if (!p.title.trim() && !p.project) return 'Nothing to save yet.';
      return '<span style="font-family:var(--font-technical);">&#8629;</span> saves it to your backlog. It will not appear on anyone&rsquo;s Today until a day is chosen.';
    }
    if (state.mode === 'ask') {
      if (state.autoswitched) return 'Switched to Ask Track on its own, words intact. <span style="font-family:var(--font-technical);">&#8677;</span> goes back to Find.';
      if (isRecordQuestion(q)) {
        const t = state.data ? noteTarget() : null;
        if (t && t.project) return `<span style="font-family:var(--font-technical);">&#8629;</span> answers from ${escapeHtml(String(t.project.number || 'the project'))}&rsquo;s own records and opens the thread at the one it cites.`;
        return '<span style="font-family:var(--font-technical);">&#8629;</span> asks the standards library. Name a project number to ask its own records instead.';
      }
      return '<span style="font-family:var(--font-technical);">&#8629;</span> opens ASP knowledge with the answer. The table, the passages and the register do not fit here.';
    }
    if (state.mode === 'note') {
      if (state.noteError) return `Not saved \u2014 ${escapeHtml(state.noteError)}. The words are still here; try again.`;
      if (!q) return 'The screen you came from is the first guess. Type a number or a name to aim it somewhere else.';
      if (state.noteTarget) return '<span style="font-family:var(--font-technical);">&#8629;</span> saves it to the project record, stamped with your name and the time.';
      return 'No project on the screen behind, and nothing in the words. It will not save until it has somewhere to land.';
    }
    if (!q) return '<span style="font-family:var(--font-technical);">&#8677;</span> to ask the standards library';
    if (!state.results.length) return 'No record matches. <span style="font-family:var(--font-technical);">&#8677;</span> to ask the standards library instead.';
    return `${state.results.length} record${state.results.length === 1 ? '' : 's'}. <span style="font-family:var(--font-technical);">&#8629;</span> opens the highlighted one.`;
  }

  const small = () => window.innerWidth <= 760;   // the shell's breakpoint (28/08)

  function paint() {
    if (!state.openState) {
      // v4 (design p44): no corner disc. ⌘K and Home's search field open the palette.
      surface.innerHTML = '';
      root.classList.remove('ab-open');
      return;
    }
    root.classList.add('ab-open');
    const sheet = small();
    const tabs = MODES.map((m) => `<button type="button" class="tab askbar-mode${state.mode === m ? ' active' : ''}" data-m="${m}">${MODE_LABEL[m]}</button>`).join('');
    surface.innerHTML = `
      <div class="askbar-panel ab4${sheet ? ' is-sheet' : ''}" role="dialog" aria-label="Search and do">
        <div class="ab4-input">
          <span class="ab4-glyph" aria-hidden="true">${navIcon('search', 16)}</span>
          <textarea class="askbar-field" rows="1" placeholder="${escapeHtml(PLACEHOLDER[state.mode])}">${escapeHtml(state.q)}</textarea>
          <kbd class="ab4-kbd">Esc</kbd>
        </div>
        <div class="tabs ab4-modes">${tabs}<span class="ab4-auto askbar-auto">${state.held ? 'mode held' : `auto &#183; ${escapeHtml(MODE_LABEL[state.mode] || state.mode)}`}</span></div>
        <div class="askbar-body ab4-body">${bodyHtml()}</div>
        <div class="ab4-foot">
          <span class="askbar-foot-msg">${footerHtml()}</span>
          <span class="ab4-keys"><kbd>&#8593;</kbd><kbd>&#8595;</kbd> move <kbd>Enter</kbd> do it <kbd>Tab</kbd> mode</span>
          <button class="askbar-report linkbtn" title="Report a problem or suggestion">Report an issue</button>
        </div>
      </div>`;

    const field = surface.querySelector('.askbar-field');
    // Empty: one line, whatever the placeholder would wrap to (it was sizing
    // the box to a two-line placeholder). Typed text still grows to 96px.
    const grow = () => { field.style.height = 'auto'; if (field.value) field.style.height = `${Math.min(112, field.scrollHeight)}px`; };
    grow();
    field.addEventListener('input', () => {
      state.q = field.value;
      state.autoswitched = null;
      // Emptying the field releases a held mode — a fresh start is a fresh
      // guess, which is the only moment re-guessing is not annoying.
      if (!state.q.trim()) state.held = false;
      state.ask = null;   // a new question is not an answer to the old one
      if (!state.held && state.mode !== 'task') {
        const m = inferMode(state.q);
        if (m !== state.mode) switchMode(m, { auto: true });
      }
      if (state.mode === 'find') runFind();
      if (state.mode === 'note') { state.noteTarget = noteTarget(); ensureTargetInstructions(); }
      grow();
      repaintKeepingField();
    });
    field.addEventListener('keydown', onFieldKeys);
    surface.querySelectorAll('.askbar-mode').forEach((t) => t.addEventListener('click', () => switchMode(t.dataset.m)));
    surface.querySelectorAll('.askbar-row[data-i]').forEach((row) => {
      row.addEventListener('mousedown', (ev) => { ev.preventDefault(); onRowPick(Number(row.dataset.i)); });
    });
    surface.querySelectorAll('.askbar-instr').forEach((row) => row.addEventListener('mousedown', (ev) => {
      ev.preventDefault();
      if (state.noteTarget) { state.noteTarget.seq = Number(row.dataset.seq); repaintKeepingField(); }
    }));
    surface.querySelector('.askbar-ask-go')?.addEventListener('click', () => submitAsk());
    surface.querySelector('.askbar-ask-full')?.addEventListener('click', () => {
      const q = state.q.trim(); close(); open({ type: 'knowledge', question: q });
    });
    surface.querySelector('.askbar-report')?.addEventListener('click', () => { close(); openReportDialog(); });
    const selectAll = state.selectOnOpen; state.selectOnOpen = false;
    setTimeout(() => {
      field.focus();
      if (selectAll && field.value) field.select();   // the previous search, highlighted: type to replace it
      else field.setSelectionRange(field.value.length, field.value.length);
    }, 0);
  }

  // Repaint the body + footer only, so the field keeps focus and caret.
  function repaintKeepingField() {
    const body = surface.querySelector('.askbar-body');
    if (!body) { paint(); return; }
    body.innerHTML = bodyHtml();
    surface.querySelectorAll('.askbar-row[data-i]').forEach((row) => {
      row.addEventListener('mousedown', (ev) => { ev.preventDefault(); onRowPick(Number(row.dataset.i)); });
    });
    surface.querySelectorAll('.askbar-instr').forEach((row) => row.addEventListener('mousedown', (ev) => {
      ev.preventDefault();
      if (state.noteTarget) { state.noteTarget.seq = Number(row.dataset.seq); repaintKeepingField(); }
    }));
    surface.querySelector('.askbar-ask-go')?.addEventListener('click', () => submitAsk());
    const foot = surface.querySelector('.askbar-foot-msg');
    if (foot) foot.innerHTML = footerHtml();
    // mode tabs may have moved (autoswitch)
    surface.querySelectorAll('.askbar-mode').forEach((t) => t.classList.toggle('active', t.dataset.m === state.mode));
    const field = surface.querySelector('.askbar-field');
    if (field) field.placeholder = PLACEHOLDER[state.mode];
    // …and so may the strip's right-hand state (§7): it says which mode Track
    // chose and whether ⇥ has pinned it. It was the one thing here still
    // rendered from the last full paint, so it sat on "auto · find" while the
    // tabs beside it had already moved to Ask Track — the label that exists to
    // explain the switch, contradicting the switch.
    const autoLabel = surface.querySelector('.askbar-auto');
    if (autoLabel) autoLabel.innerHTML = state.held ? 'mode held' : `auto &#183; ${escapeHtml(MODE_LABEL[state.mode] || state.mode)}`;
  }

  function switchMode(m, { auto = false } = {}) {
    if (!MODES.includes(m) || m === state.mode) return;
    state.mode = m;
    if (!auto) state.autoswitched = null;
    sessionStorage.setItem(MODE_KEY, m);
    if (m === 'find') runFind();
    if (m === 'note') { state.noteTarget = noteTarget(); ensureTargetInstructions(); ensureWeekProjects(); }
    if (m === 'ask') ensureData();
    if (m === 'task') { state.taskPick = 0; state.taskError = null; ensureData(); }
    repaintKeepingField();
  }

  function onFieldKeys(e) {
    const field = e.target;
    if (e.key === 'Tab') {
      // ⇥ cycles, never losing what is typed, and HOLDS the mode from then on
      // (§7) — Track has been corrected and stops guessing.
      e.preventDefault();
      state.held = true;
      switchMode(MODES[(MODES.indexOf(state.mode) + 1) % MODES.length]);
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown' && state.mode === 'find') { e.preventDefault(); state.sel = Math.min(state.sel + 1, Math.max(0, (state.q.trim() ? state.results.length : recents().length) - 1)); repaintKeepingField(); return; }
    if (e.key === 'ArrowUp' && state.mode === 'find') { e.preventDefault(); state.sel = Math.max(state.sel - 1, 0); repaintKeepingField(); return; }
    if (state.mode === 'task' && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      const amb = taskParse().ambiguous;
      if (amb) {
        e.preventDefault();
        const n = amb.matches.length + 1;   // + "leave unlinked", always last
        state.taskPick = (state.taskPick + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
        repaintKeepingField();
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (state.mode === 'find') onRowPick(state.sel);
      else if (state.mode === 'ask') submitAsk();
      else if (state.mode === 'note' && state.noteTarget) saveNote(state.noteTarget);
      else if (state.mode === 'task') { if (taskParse().ambiguous) taskChoose(); else saveTask(); }
    }
  }

  function onRowPick(i) {
    if (state.mode === 'note') {
      // picking a nearby project aims the note
      const nearby = state.weekProjects || [];
      const p = nearby[i];
      if (p) { state.noteTarget = { project: p, how: 'picked', strip: null }; repaintKeepingField(); }
      return;
    }
    const list = state.q.trim() ? state.results : recents();
    const r = list[i];
    if (!r) return;
    const question = state.q.trim();
    remember(r);
    close();
    if (isRecordQuestion(question) && (r.type === 'project' || r.type === 'instruction')) {
      const project = (state.data || {}).projById?.[r.projectId || r.id];
      if (project) {
        openRecordThread(project, question)
          .catch((e) => { console.warn('[ask] thread —', e.message); open(r); });
        return;
      }
    }
    open(r);
  }

  // Open the thread on a project with the question pinned. The answer arrives
  // into the open panel; nothing waits on the model before the records show.
  function openRecordThread(project, question) {
    const d = state.data || {};
    const cid = refTail(project.clientRef);
    const client = cid ? (d.clients || []).find((c) => c.id === cid) : null;
    return openProjectThread({ project, client: client || null, session, isMoney, answer: { question } });
  }

  function submitAsk() {
    const q = state.q.trim();
    if (q.length < 6) return;
    // Asked about one job, in words about that job: the records answer it, not
    // the library. `noteTarget` is the same resolution a note uses — a number
    // typed, a client named, or the screen you came from.
    if (isRecordQuestion(q)) {
      const t = noteTarget();
      if (t && t.project) {
        close();
        openRecordThread(t.project, q).catch((e) => { console.warn('[ask] thread —', e.message); open({ type: 'knowledge', question: q }); });
        return;
      }
    }
    // FULL PAGE (Karan 15/10, reversing the 24/09 answer-in-the-panel): an
    // engineering answer needs its table, passages and page references, which
    // do not fit in the corner. Enter opens Ask Track's own page with the
    // question. The in-panel answer (askAnswerHtml) is kept but not reached.
    close();
    open({ type: 'knowledge', question: q });
  }

  function openPanel({ mode } = {}) {
    state.prevFocus = document.activeElement;
    state.openState = true;
    state.autoswitched = null;
    if (mode && MODES.includes(mode)) { state.mode = mode; sessionStorage.setItem(MODE_KEY, mode); }
    if (state.mode === 'note') { state.noteTarget = noteTarget(); ensureWeekProjects(); }
    ensureData();
    state.selectOnOpen = true;   // Karan 28/10/2026: reopening shows the last search highlighted, so typing replaces it
    paint();
  }
  function close() {
    if (!state.openState) return;
    state.openState = false;
    const panel = surface.querySelector('.askbar-panel');
    if (panel) {
      panel.style.animation = `askbar-close 120ms ${EASE} both`;
      setTimeout(() => paint(), 120);
    } else paint();
    // ESC returns focus to where it was, on every screen (acceptance 8).
    if (state.prevFocus && state.prevFocus.focus && document.contains(state.prevFocus)) state.prevFocus.focus();
    state.prevFocus = null;
  }

  // ⌘K / Ctrl K TOGGLES, everywhere (§7). It used to only open, which meant the
  // chord that summoned the panel could not dismiss it — you had to reach for
  // Esc or the mouse. (Replaces the old pill focus AND the pilot command
  // palette binding — Find covers the palette's jumps.)
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      if (state.openState) { close(); return; }
      openPanel();
    }
  });
  // The panel closes on a route change (§7): it is a way to get somewhere, and
  // leaving it open over the screen it just took you to is clutter.
  document.addEventListener('track:route', () => { if (state.openState) close(); });
  document.addEventListener('pointerdown', (e) => {
    if (state.openState && !root.contains(e.target)) close();
  });
  // As a bottom sheet the root IS the scrim — it covers the screen, so "outside
  // the root" never happens and the rule above can never fire. A press on the
  // root itself, rather than on the panel, is a press on the scrim.
  root.addEventListener('pointerdown', (e) => {
    if (state.openState && e.target === root) close();
  });

  // Anything can ask for quick-add without knowing it lives in this bar — the
  // Today panel's footer and its empty state both do. One event, one answer.
  document.addEventListener('track:quick-add', () => {
    if (state.openState) { switchMode('task'); surface.querySelector('.askbar-field')?.focus(); return; }
    openPanel({ mode: 'task' });
  });

  // Motion keyframes for open/close (20b: scale from bottom right, no bounce).
  if (!document.getElementById('askbar-anim')) {
    const st = document.createElement('style');
    st.id = 'askbar-anim';
    st.textContent = `@keyframes askbar-open{from{opacity:0;transform:scale(0.35)}to{opacity:1;transform:scale(1)}}
@keyframes askbar-close{from{opacity:1;transform:scale(1)}to{opacity:0;transform:scale(0.85)}}
.office-mode .askbar-root{display:none !important}
@media (max-width:760px){.askbar-root{right:14px}}`;
    document.head.appendChild(st);
  }

  paint();
  return { open: openPanel, close, setContext: setAskContext };
}
